import { Grid } from '@mui/material';
import { Cropper } from 'shell/components/shared/cropper';
import { memo, useCallback } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { PageMode } from 'civil-status/view-models/page-mode';
import ButtonsForm from '../shared/buttons';
import DescriptionForm from '../shared/description-document';
import AnnotationForm from '../shared/annotation';
import UploadFileForm from '../shared/upload-file';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import CitizenshipFormComponent from '../shared/citizenship-form';
import { DocumentCitizenshipFormVM } from 'civil-status/view-models/document-citizenship-form';

interface DocumentBirthFormProps {
  values?: DocumentCitizenshipFormVM;
  errors?: FormikErrors<DocumentCitizenshipFormVM>;
  touched?: FormikTouched<DocumentCitizenshipFormVM>;
  onChange: (
    key: keyof DocumentCitizenshipFormVM,
    value: DocumentCitizenshipFormVM[keyof DocumentCitizenshipFormVM]
  ) => void;
  onChanges: (value: DocumentCitizenshipFormVM) => void;
  mode: PageMode;
  onDetailMode: () => void;
  onEditMode: () => void;
  onSubstitutionMode: () => void;
  onAnnotationNationalMode: () => void;
  onAnnotationInternationalMode: () => void;
  onPrintMode: () => void;
  type: string;
}

const DocumentBirthComponent = ({
  values,
  touched,
  errors,
  mode,
  type,
  onChange,
  onChanges,
  onDetailMode,
  onEditMode,
  onSubstitutionMode,
  onPrintMode,
  onAnnotationNationalMode,
  onAnnotationInternationalMode,
}: DocumentBirthFormProps): React.ReactElement => {
  const deleteAll = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi/all`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const handleDeleteAllInternational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: type,
            numeroRegistro: values?.registerNumber,
            anno: values?.year,
            numeroAtto: values?.documentNumber,
            parte: values?.portion,
            serie1: values?.serie,
          },
          tipoCertificato: 'INT',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      onDetailMode();
    });
  }, [deleteAll, onDetailMode, values, type]);

  const handleDeleteAllNational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: type,
            numeroRegistro: values?.registerNumber,
            anno: values?.year,
            numeroAtto: values?.documentNumber,
            parte: values?.portion,
            serie1: values?.serie,
          },
          tipoCertificato: 'NAZ',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      onChange('annotationNationalList', []);
      onDetailMode();
    });
  }, [values, type, deleteAll, onDetailMode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <ButtonsForm
          mode={mode}
          onAnnotationInternationalMode={onAnnotationInternationalMode}
          onAnnotationNationalMode={onAnnotationNationalMode}
          onDetailMode={onDetailMode}
          onEditMode={onEditMode}
          onPrintMode={onPrintMode}
          onSubstitutionMode={onSubstitutionMode}
          onAnnotationInternationalDeleteAll={handleDeleteAllInternational}
          onAnnotationNationalDeleteAll={handleDeleteAllNational}
          url="/stato-civile/cittadinanza/ricerca"
        />
        <DescriptionForm values={values} />
        <UploadFileForm values={values} onChange={onChange} mode={mode} />
        {mode === PageMode.Detail || mode === PageMode.Edit ? (
          <CitizenshipFormComponent
            onChange={onChange}
            onChanges={onChanges}
            values={values}
            errors={errors}
            mode={mode}
            touched={touched}
          />
        ) : undefined}
        <AnnotationForm
          values={values}
          onChange={onChange}
          mode={mode}
          type={type}
          setMode={onDetailMode}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <StickyDiv>
          <Cropper
            images={values?.uploadFile?.raw || []}
            disabled={!values?.uploadFile?.raw?.length}
            height={755}
          />
        </StickyDiv>
      </Grid>
    </Grid>
  );
};

DocumentBirthComponent.displayName = 'Document Citizenship form component';

const DocumentBirthForm = memo(DocumentBirthComponent);

export default DocumentBirthForm;
